"use client";

import { useEffect, useSyncExternalStore } from "react";
import { X } from "lucide-react";
import { Alert } from "@/components/alert";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n";

type ToastTone = "success" | "error";
type ToastAction = { label: string; onClick: () => void };
type Toast = {
  id: number;
  tone: ToastTone;
  text: string;
  action?: ToastAction;
};

// One list for the whole app, outside React, so an outcome can be reported
// from wherever the action ran without threading a provider to it.
let toasts: Toast[] = [];
let nextId = 1;
const listeners = new Set<() => void>();

function emit(next: Toast[]) {
  toasts = next;
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function snapshot() {
  return toasts;
}

function push(tone: ToastTone, text: string, action?: ToastAction) {
  const id = nextId++;
  emit([...toasts, { id, tone, text, action }]);
  return id;
}

export const toast = {
  success: (text: string) => push("success", text),
  error: (text: string, action?: ToastAction) => push("error", text, action),
  dismiss: (id: number) => emit(toasts.filter((item) => item.id !== id)),
  clear: () => emit([]),
};

// An error stays longer than a success: it is the one the reader may need to
// read twice before acting on it.
const lifetime: Record<ToastTone, number> = { success: 6000, error: 10000 };

function ToastItem({ item, closeLabel }: { item: Toast; closeLabel: string }) {
  useEffect(() => {
    const timer = setTimeout(() => toast.dismiss(item.id), lifetime[item.tone]);
    return () => clearTimeout(timer);
  }, [item.id, item.tone]);
  return (
    <Alert tone={item.tone} title={item.text} className="toast shadow-lg">
      {item.action ? (
        <Button
          variant="link"
          size="inline"
          className="text-current underline hover:text-current"
          onClick={() => {
            toast.dismiss(item.id);
            item.action?.onClick();
          }}
        >
          {item.action.label}
        </Button>
      ) : null}
      <button
        type="button"
        className="toast-dismiss"
        aria-label={closeLabel}
        onClick={() => toast.dismiss(item.id)}
      >
        <X size={14} aria-hidden="true" />
      </button>
    </Alert>
  );
}

/** Mounted once at the root; every toast() call lands here. */
export function Toaster() {
  const { t } = useI18n();
  const items = useSyncExternalStore(subscribe, snapshot, snapshot);
  if (!items.length) return null;
  return (
    <div className="toaster">
      {items.map((item) => (
        <ToastItem key={item.id} item={item} closeLabel={t("auth.close")} />
      ))}
    </div>
  );
}
